import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { sessionQueryKey } from '@/entities/session';
import { login } from '@/shared/api/auth';
import type { User } from '@/shared/api/types';
import { Button, ErrorMessage, PasswordField, TextField } from '@/shared/ui';

import styles from './forms.module.css';

/** Signs in with an email or username; fills the session cache on success. */
export function LoginForm({ onSuccess }: { onSuccess?: (user: User) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => login({ login: identifier, password }),
    onSuccess: (user) => {
      queryClient.setQueryData(sessionQueryKey, user);
      onSuccess?.(user);
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label={t('auth.login')}
        autoComplete="username"
        autoCapitalize="none"
        required
        value={identifier}
        onChange={(event) => {
          setIdentifier(event.target.value);
        }}
      />
      <PasswordField
        label={t('auth.password')}
        autoComplete="current-password"
        required
        value={password}
        onChange={(event) => {
          setPassword(event.target.value);
        }}
      />
      <div className={styles.row}>
        <Link to="/forgot-password">{t('auth.forgot')}</Link>
      </div>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {t('auth.submitLogin')}
      </Button>
    </form>
  );
}
